import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";

import { CoachPhoto } from "../../components/coach-photo";
import {
  Section,
  SectionHead,
  card,
  ghostButton,
  label,
  peopleColumns,
  primaryOrb,
  well,
} from "../../components/section";
import { sitePath, useSiteConfig } from "../../hooks/use-site-config";
import type { PublicDirectory } from "../../types";
import { nextOpenTime } from "../../utils/directory";
import { coachAnchor, openTimeLabel } from "../../utils/labels";
import { SlotGateDialog } from "../open-calendar/slot-gate-dialog";
import { useSlotGate } from "../open-calendar/use-slot-gate";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Coaches shown before the rest fold behind "Meet the team". */
const VISIBLE = 4;

/**
 * The coaches, one card each: who they are and their next open time with a button that takes it straight to the sign-in step. The name opens the full
 * profile. Without a directory there is nobody to show, so the section stays out of the page.
 */
export const Team = ({ directory }: { directory: PublicDirectory | null }) => {
  const { slug } = useSiteConfig();
  const { open, gate } = useSlotGate(directory);
  if (!directory || directory.specialists.length === 0) return null;

  return (
    <Section id="team">
      <SectionHead
        title={m.landing_team_title()}
        aside={
          <Link to={sitePath(slug, "coaches")} className={ghostButton}>
            {m.landing_team_all()}
          </Link>
        }
      />
      <div className={cn("grid gap-3 sm:grid-cols-2", peopleColumns(Math.min(directory.specialists.length, VISIBLE)))}>
        {directory.specialists.slice(0, VISIBLE).map((specialist) => {
          const next = nextOpenTime(specialist);
          const when = next ? openTimeLabel(next, directory) : null;

          return (
            <article key={specialist.id} className={cn(card, "flex flex-col")}>
              <Link
                to={sitePath(slug, "coaches")}
                hash={coachAnchor(specialist.id)}
                className="group flex items-center gap-[18px] rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <CoachPhoto
                  name={specialist.name}
                  src={specialist.avatarUrl}
                  className="size-[84px] rounded-3xl text-2xl"
                />
                <span className="min-w-0">
                  <b className="block truncate text-2xl font-medium tracking-[-0.03em] group-hover:underline">
                    {specialist.name}
                  </b>
                  <span className="block truncate text-[var(--site-ink)]">{specialist.title}</span>
                </span>
              </Link>

              <div className="h-6" />

              <div className={cn(well, "mt-auto flex items-center justify-between gap-3 py-3 pr-3 pl-[18px]")}>
                <div className="min-w-0">
                  <span className={label}>{m.landing_team_next()}</span>
                  <b className="block truncate font-semibold">{when ?? m.landing_team_full()}</b>
                </div>
                {next && when ? (
                  <button
                    type="button"
                    aria-label={m.landing_team_book({ name: specialist.name, when })}
                    className={primaryOrb}
                    onClick={() =>
                      open({ specialistId: specialist.id, serviceId: specialist.serviceId, startAt: next })
                    }
                  >
                    <ArrowUpRight aria-hidden className="size-[18px]" />
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
      <SlotGateDialog {...gate} />
    </Section>
  );
};
